// Phase 8 R2 — Lines editor with add / remove / move up/down / save.
// Local working copy; saves atomically via replace_canned_job_lines RPC.

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ArrowDown, ArrowUp, Plus, Trash2, Loader2, Wrench, Package, Link2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useCannedJobLines,
  type CannedJobLine,
} from '@/hooks/cannedJobs/useCannedJobLines';
import {
  useReplaceCannedJobLines,
  type CannedJobLineInput,
} from '@/hooks/cannedJobs/useReplaceCannedJobLines';
import {
  useCatalogJobSearch,
  useCatalogJobByCode,
  type CatalogSearchRow,
} from '@/hooks/cannedJobs/useCatalogJobSearch';
import {
  resolveCannedLineMetadata,
  type CatalogMetaRow,
} from '@/lib/cannedJobs/resolveCannedLineMetadata';

interface DraftLine {
  key: string;
  line_type: 'part' | 'labor';
  part_description: string;
  part_number: string;
  quantity: string;
  labor_description: string;
  labor_hours: string;
  /** CAT-1/CAT-4: canonical catalog code a labor line is bound to. */
  catalog_job_code: string | null;
}

interface Props {
  cannedJobId: string;
  jobIsActive: boolean;
  /** Free-text canned_jobs.category — used to preview the custom fallback. */
  cannedCategory?: string | null;
}

const newKey = () => Math.random().toString(36).slice(2);

const fromServer = (l: CannedJobLine): DraftLine => ({
  key: l.id,
  line_type: l.line_type,
  part_description: l.part_description ?? '',
  part_number: l.part_number ?? '',
  quantity: l.quantity != null ? String(l.quantity) : '',
  labor_description: l.labor_description ?? '',
  labor_hours: l.labor_hours != null ? String(l.labor_hours) : '',
  catalog_job_code: l.catalog_job_code ?? null,
});

const validateLine = (l: DraftLine): string | null => {
  if (l.line_type === 'part') {
    if (!l.part_description.trim()) return 'Description required';
    const q = Number(l.quantity);
    if (!isFinite(q) || q <= 0) return 'Quantity > 0';
  } else {
    if (!l.labor_description.trim()) return 'Description required';
    const h = Number(l.labor_hours);
    if (!isFinite(h) || h <= 0) return 'Hours > 0';
  }
  return null;
};

const toRpc = (l: DraftLine): CannedJobLineInput =>
  l.line_type === 'part'
    ? {
        line_type: 'part',
        part_description: l.part_description.trim(),
        part_number: l.part_number.trim() || null,
        quantity: Number(l.quantity),
      }
    : {
        line_type: 'labor',
        labor_description: l.labor_description.trim(),
        labor_hours: Number(l.labor_hours),
        catalog_job_code: l.catalog_job_code,
      };

// CAT-1/CAT-4 — per-labor-line catalog binder + live "routes to" preview.
// The preview is rendered from the SAME pure resolver the apply RPC
// mirrors, so authors see real routing (canonical category / bay / skill /
// minutes) instead of the old hardcoded 'Canned' · 'Flat' · skill 1.
function CatalogLineBinder({
  code,
  laborHours,
  cannedCategory,
  onBind,
  onClear,
}: {
  code: string | null;
  laborHours: string;
  cannedCategory: string | null;
  onBind: (row: CatalogSearchRow) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data: results = [], isFetching } = useCatalogJobSearch(query);
  const { data: boundRow } = useCatalogJobByCode(code);

  const catalogMeta: CatalogMetaRow | null = boundRow ?? null;
  const preview = resolveCannedLineMetadata(
    { laborHours: Number(laborHours) || null, catalogJobCode: code },
    catalogMeta,
    cannedCategory,
  );

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" size="sm" variant="outline" className="h-7">
            <Link2 className="h-3.5 w-3.5 mr-1" />
            {code ? 'Change catalog job' : 'Link catalog job'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[360px]" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search catalog (label or code)…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {isFetching && (
                <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
              )}
              <CommandEmpty>No catalog jobs match.</CommandEmpty>
              <CommandGroup>
                {results.map((r) => (
                  <CommandItem
                    key={r.code}
                    value={r.code}
                    onSelect={() => {
                      onBind(r);
                      setOpen(false);
                    }}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">{r.label}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {r.category_key} · {r.sub_menu} · {r.minutes_default ?? '—'}m
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {code ? (
        <>
          <Badge variant="outline" className="text-[10px]">
            {preview.source === 'catalog'
              ? `Routes to ${preview.category} · ${preview.bayRequirement} · skill ${preview.skillLevel} · ${preview.standardMinutes}m`
              : `Catalog code ${code}`}
          </Badge>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={onClear}
            aria-label="Unlink catalog job"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </>
      ) : (
        <span className="text-[11px] text-muted-foreground">
          Custom (unverified): routes to {preview.category} · skill {preview.skillLevel}
        </span>
      )}
    </div>
  );
}

export function CannedJobLinesEditor({ cannedJobId, jobIsActive, cannedCategory = null }: Props) {
  const { data: serverLines = [], isLoading } = useCannedJobLines(cannedJobId);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [dirty, setDirty] = useState(false);

  // Reset draft when canned job changes or server data first loads.
  useEffect(() => {
    setDraft(serverLines.map(fromServer));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cannedJobId, serverLines.length, serverLines.map((l) => l.id).join(',')]);

  const validations = useMemo(() => draft.map(validateLine), [draft]);
  const hasInvalid = validations.some((v) => v !== null);
  const isEmpty = draft.length === 0;
  const blockEmptyOnActive = isEmpty && jobIsActive;

  const replaceMut = useReplaceCannedJobLines();

  const update = (idx: number, patch: Partial<DraftLine>) => {
    setDraft((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    setDirty(true);
  };

  const addLine = (type: 'part' | 'labor') => {
    setDraft((prev) => [
      ...prev,
      {
        key: newKey(),
        line_type: type,
        part_description: '',
        part_number: '',
        quantity: type === 'part' ? '1' : '',
        labor_description: '',
        labor_hours: type === 'labor' ? '1' : '',
        catalog_job_code: null,
      },
    ]);
    setDirty(true);
  };

  const remove = (idx: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const move = (idx: number, dir: -1 | 1) => {
    setDraft((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (hasInvalid || blockEmptyOnActive) return;
    try {
      await replaceMut.mutateAsync({
        cannedJobId,
        lines: draft.map(toRpc),
      });
      setDirty(false);
    } catch {
      // toast handled in hook
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Lines</CardTitle>
          <CardDescription>
            Parts and labor expanded onto the ticket when this canned job is applied.
          </CardDescription>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => addLine('part')}
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Part
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => addLine('labor')}
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Labor
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading lines…
          </div>
        ) : draft.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-md">
            No lines yet. Add a Part or Labor line to get started.
          </div>
        ) : (
          <div className="space-y-2">
            {draft.map((line, idx) => {
              const err = validations[idx];
              return (
                <div
                  key={line.key}
                  className={cn(
                    'rounded-md border p-3 bg-card',
                    err && 'border-destructive/60',
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px] uppercase">
                        {line.line_type === 'part' ? (
                          <>
                            <Package className="h-3 w-3 mr-1" /> Part
                          </>
                        ) : (
                          <>
                            <Wrench className="h-3 w-3 mr-1" /> Labor
                          </>
                        )}
                      </Badge>
                      <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                      {err && (
                        <span className="text-xs text-destructive">{err}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        aria-label="Move up"
                        className="h-7 w-7"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => move(idx, 1)}
                        disabled={idx === draft.length - 1}
                        aria-label="Move down"
                        className="h-7 w-7"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(idx)}
                        aria-label="Remove"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {line.line_type === 'part' ? (
                    <div className="grid grid-cols-12 gap-2">
                      <Input
                        placeholder="Part description"
                        value={line.part_description}
                        onChange={(e) =>
                          update(idx, { part_description: e.target.value })
                        }
                        className="col-span-7"
                      />
                      <Input
                        placeholder="Part #"
                        value={line.part_number}
                        onChange={(e) =>
                          update(idx, { part_number: e.target.value })
                        }
                        className="col-span-3"
                      />
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="Qty"
                        value={line.quantity}
                        onChange={(e) => update(idx, { quantity: e.target.value })}
                        className="col-span-2"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-12 gap-2">
                        <Input
                          placeholder="Labor description"
                          value={line.labor_description}
                          onChange={(e) =>
                            update(idx, { labor_description: e.target.value })
                          }
                          className="col-span-9"
                        />
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="Hours"
                          value={line.labor_hours}
                          onChange={(e) => update(idx, { labor_hours: e.target.value })}
                          className="col-span-3"
                        />
                      </div>
                      <CatalogLineBinder
                        code={line.catalog_job_code}
                        laborHours={line.labor_hours}
                        cannedCategory={cannedCategory}
                        onBind={(row) =>
                          update(idx, {
                            catalog_job_code: row.code,
                            // CAT-4: adopt the canonical label when blank.
                            labor_description: line.labor_description.trim()
                              ? line.labor_description
                              : row.label,
                            // CAT-5: seed hours from the catalog default when
                            // the author has not entered a specific duration.
                            labor_hours:
                              !line.labor_hours || Number(line.labor_hours) <= 0 || line.labor_hours === '1'
                                ? row.minutes_default && row.minutes_default > 0
                                  ? String(Math.round((row.minutes_default / 60) * 10) / 10)
                                  : line.labor_hours
                                : line.labor_hours,
                          })
                        }
                        onClear={() => update(idx, { catalog_job_code: null })}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {blockEmptyOnActive && (
          <div className="text-xs text-destructive">
            Active canned jobs must have at least one line. Deactivate first to clear all lines.
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-muted-foreground">
            {dirty ? 'Unsaved changes' : 'Up to date'}
          </div>
          <Button
            type="button"
            onClick={handleSave}
            disabled={
              !dirty ||
              hasInvalid ||
              blockEmptyOnActive ||
              replaceMut.isPending
            }
          >
            {replaceMut.isPending && (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            )}
            Save Lines
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
